import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart as LineChartIcon, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, Cell, CartesianGrid,
} from "recharts";
import { useChartTheme } from "@/lib/chartTheme";

const BAND = (score) =>
  score >= 70 ? { key: "Strong", color: "#10B981" }
  : score >= 45 ? { key: "Competitive", color: "#003E87" }
  : score >= 25 ? { key: "At risk", color: "#F59E0B" }
  : { key: "Long shot", color: "#E11D48" };

const SUMMARY = [
  ["strong", "Strong", "#10B981"],
  ["competitive", "Competitive", "#003E87"],
  ["at_risk", "At risk", "#F59E0B"],
  ["long_shot", "Long shot", "#E11D48"],
];

function DotTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs max-w-xs">
      <div className="font-semibold text-black">{p.client}</div>
      <div className="mt-1 text-slate-600">Average win likelihood <b style={{ color: BAND(p.score).color }}>{BAND(p.score).key} · {p.score}%</b> across {p.sites.length} live bid{p.sites.length === 1 ? "" : "s"}</div>
      <ul className="mt-1 space-y-0.5">
        {p.sites.slice(0, 10).map((s) => <li key={s.bid_id} className="text-slate-500 flex justify-between gap-3"><span className="truncate">{s.name}</span><span className="tabular-nums" style={{ color: BAND(s.score).color }}>{s.score}%</span></li>)}
        {p.sites.length > 10 && <li className="text-slate-400">+{p.sites.length - 10} more</li>}
      </ul>
    </div>
  );
}

export default function WinTrend() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const ct = useChartTheme();
  useEffect(() => { api.get("/insights/win-trend").then((r) => setData(r.data)).catch(() => {}); }, []);

  const [showAll, setShowAll] = useState(false);
  const chartData = useMemo(() => {
    const byClient = {};
    for (const p of data?.points || []) {
      const k = p.client || "No client";
      const g = byClient[k] || (byClient[k] = { client: k, sites: [], sum: 0 });
      g.sites.push(p); g.sum += Number(p.score || 0);
    }
    const rows = Object.values(byClient).map((g) => ({ ...g, score: Math.round(g.sum / g.sites.length), short: g.client.length > 18 ? g.client.slice(0, 17) + "…" : g.client, bid_id: g.sites.length === 1 ? g.sites[0].bid_id : null }))
      .sort((a, b) => b.score - a.score);
    return showAll ? rows : rows.slice(0, 20);
  }, [data, showAll]);
  const clientCount = useMemo(() => new Set((data?.points || []).map((p) => p.client || "No client")).size, [data]);

  return (
    <div className="px-6 sm:px-8 py-8" data-testid="win-trend-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
          <LineChartIcon className="w-5 h-5 text-[#003E87]" /> Win trend
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Win-likelihood across the active book — how aggressively each live bid is priced against the won-bid distribution. Higher is safer; lower is sharper.</p>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : data.count === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="win-trend-empty">
          No active bids with enough won comparables to score yet.
        </Card>
      ) : (
        <>
          <Card className="p-6 mb-6 flex flex-wrap items-center gap-8" data-testid="win-trend-summary">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold text-black tabular-nums" data-testid="win-trend-average">{data.average}%</span>
                <TrendingUp className="w-5 h-5 text-[#F4C400]" />
              </div>
              <div className="text-xs text-slate-500 mt-1">Book average win-likelihood — {data.count} live bids scored</div>
            </div>
            <div className="flex flex-wrap gap-4">
              {SUMMARY.map(([k, label, color]) => (
                <div key={k} className="text-center" data-testid={`win-trend-band-${k}`}>
                  <div className="font-display text-2xl font-bold tabular-nums" style={{ color }}>{data.bands[k]}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-center">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: color }} /> {label}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 mb-6" data-testid="win-trend-chart">
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={ct.gridSoft} vertical={false} />
                  <XAxis dataKey="short" tick={{ fontSize: 11, fill: ct.tickDim }} interval={0} angle={chartData.length > 8 ? -35 : 0} textAnchor={chartData.length > 8 ? "end" : "middle"} height={chartData.length > 8 ? 90 : 30} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: ct.tickDim }} unit="%" />
                  <Tooltip content={<DotTooltip />} cursor={{ fill: ct.cursor }} />
                  {data.average != null && (
                    <ReferenceLine y={data.average} stroke="#F4C400" strokeWidth={2} strokeDasharray="5 4"
                      label={{ value: `avg ${data.average}%`, position: "right", fill: ct.dark ? "#F4C400" : "#946f00", fontSize: 11 }} />
                  )}
                  <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={48} cursor="pointer"
                    onClick={(d) => d?.bid_id && nav(`/bids/${d.bid_id}`)}>
                    {chartData.map((p) => <Cell key={p.client} fill={BAND(p.score).color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
              <span>One bar per client (average of its live bids). Hover a bar to see the sites.</span>
              {clientCount > 20 && (
                <button type="button" onClick={() => setShowAll((v) => !v)} data-testid="win-trend-show-all" className="text-[#003E87] hover:underline">
                  {showAll ? "Show top 20" : `Show all ${clientCount} clients`}
                </button>
              )}
            </div>
          </Card>

          <Card className="p-5" data-testid="win-trend-table">
            <h3 className="text-sm font-semibold text-black mb-3">Live bids, sharpest first</h3>
            <div className="space-y-2">
              {[...data.points].sort((a, b) => a.score - b.score).map((p) => {
                const band = BAND(p.score);
                return (
                  <button key={p.bid_id} onClick={() => nav(`/bids/${p.bid_id}`)}
                    className="w-full flex items-center gap-3 text-left rounded-md px-2 py-1.5 hover:bg-slate-50 transition-colors"
                    data-testid={`win-trend-row-${p.bid_id}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: band.color }} />
                    <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{p.name} {p.client && <span className="text-slate-400">· {p.client}</span>}</span>
                    <span className="text-[11px] text-slate-400 tabular-nums hidden sm:inline">${p.ppw}/W</span>
                    <span className="text-xs font-semibold tabular-nums w-28 text-right" style={{ color: band.color }}>{p.label} · {p.score}%</span>
                  </button>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
